/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { useQuery } from '@apollo/client';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import user from 'js/lib/user';

import { Button, Grid, InlineNotification, Typography, Typography2 } from '@coursera/cds-core';
import { MembersIcon } from '@coursera/cds-icons';

import { useThirdPartyOrganization } from 'bundles/admin-dashboard/components/ThirdPartyOrganizationProvider';
import { validateUserFilter } from 'bundles/enterprise-admin-user-filters/components/ErrorsNotification';
import { Section } from 'bundles/enterprise-admin-user-filters/components/ui';
import type { UserFilter } from 'bundles/enterprise-admin-user-filters/types';
import { mapUserFilter } from 'bundles/enterprise-admin-user-filters/utils';
import EnterpriseUserAdminViewCountByFilter from 'bundles/enterprise-admin-users/queries/EnterpriseUserAdminViewCountByFilter.graphql';
import type {
  EnterpriseUserAdminViewCountByFilterQuery,
  EnterpriseUserAdminViewCountByFilterQueryVariables,
} from 'bundles/enterprise-admin-users/queries/__generated__/EnterpriseUserAdminViewCountByFilter';
import { Spinner, UnauthorizedApiCallDialog, formatNumber } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/enterprise-admin-user-filters';

type PropsFromCaller = {
  userFilter: UserFilter;
  badRequest: boolean;
  onBadRequest: (bad: boolean) => void;
  onTryAgain: () => void;
};

type Props = PropsFromCaller;

export const useCountLearnersByFilter = (
  organizationId: string,
  userFilter?: UserFilter,
  contentAssignmentSearch?: string
) => {
  const { data, loading, error } = useQuery<
    EnterpriseUserAdminViewCountByFilterQuery,
    EnterpriseUserAdminViewCountByFilterQueryVariables
  >(EnterpriseUserAdminViewCountByFilter, {
    variables: {
      orgId: organizationId,
      filter: mapUserFilter(userFilter, contentAssignmentSearch),
    },
    errorPolicy: 'all',
    context: { clientName: 'gatewayGql' },
    skip: !userFilter || Object.keys(validateUserFilter(userFilter)).length > 0,
  });

  return { learnerCount: data?.EnterpriseUserAdminView?.countByFilter, loading, error };
};

export const MatchCounter = ({ userFilter, badRequest, onBadRequest, onTryAgain }: Props) => {
  const [showUnauthorizedApiCallDialog, setShowUnauthorizedApiCallDialog] = React.useState(false);
  const org = useThirdPartyOrganization();

  const { learnerCount, loading, error } = useCountLearnersByFilter(org.id, userFilter);

  // TODO: React Hook useEffect contains a call to 'setShowUnauthorizedApiCallDialog'. Without a list of dependencies, this can lead to an infinite chain of updates. To fix this, pass [error, badRequest, onBadRequest] as a second argument to the useEffect Hook.
  // FIXME: existing react-hooks/exhaustive-deps violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (error && !badRequest) {
      onBadRequest(true);
      const statusCode = (error?.networkError as $TSFixMe)?.statusCode;
      if (user.isSuperuser() && statusCode === 403) {
        setShowUnauthorizedApiCallDialog(true);
      }
    } else if (!error && badRequest) {
      onBadRequest(false);
    }
  }, [error, badRequest, onBadRequest]);

  if (badRequest) {
    return (
      <React.Fragment>
        <InlineNotification
          severity="error"
          title={_t('Error!')}
          inlineAction={
            <Button size="small" variant="ghost" onClick={onTryAgain}>
              {_t('Try again')}
            </Button>
          }
        >
          {_t('Something went wrong! Please try again later.')}
        </InlineNotification>
        <UnauthorizedApiCallDialog
          api="entuserfilter.v1beta1.*"
          open={showUnauthorizedApiCallDialog}
          onClose={() => setShowUnauthorizedApiCallDialog(false)}
        />
      </React.Fragment>
    );
  }

  return (
    <Section css={{ backgroundColor: 'var(--cds-color-blue-50)', border: 0 }}>
      <Grid container spacing={12}>
        <Grid item>
          <MembersIcon size="large" />
        </Grid>
        <Grid item>
          <Typography2 component="h3" variant="subtitleMedium">
            {_t('Selected filters match')}
          </Typography2>
          <Typography variant="h2">
            {loading ? (
              <Spinner />
            ) : (
              <FormattedMessage
                message={_t('{numOfLearners} {numOfLearners, plural, =1 {learner} other {learners}}')}
                numOfLearners={formatNumber(learnerCount)}
              />
            )}
          </Typography>
        </Grid>
      </Grid>
    </Section>
  );
};

export default MatchCounter;
